import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { FileText } from 'lucide-react';
import { z } from 'zod';

import { PolicyCreateRequest, PolicyResponse } from '@/types/service/policy';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import DocxUploadButton, { ConvertedDocx } from '@/components/policies/DocxUploadButton';
import CKEditorWrapper from '@/components/shared/CKEditorWrapper';

import { zodResolver } from '@hookform/resolvers/zod';

// Validation Schema
const PolicyFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Policy name is required')
    .max(255, 'Policy name must be less than 255 characters'),
  content: z.string().min(1, 'Policy content is required'),
});

type PolicyFormData = z.infer<typeof PolicyFormSchema>;

// Create Policy Modal
interface CreatePolicyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PolicyCreateRequest) => Promise<void>;
}

export const CreatePolicyModal = ({ open, onOpenChange, onSubmit }: CreatePolicyModalProps) => {
  const [sourceDoc, setSourceDoc] = useState<ConvertedDocx | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    getValues,
    reset,
  } = useForm<PolicyFormData>({
    resolver: zodResolver(PolicyFormSchema),
    defaultValues: {
      name: '',
      content: '',
    },
  });

  const content = watch('content');

  const handleDocxConverted = (converted: ConvertedDocx) => {
    setSourceDoc(converted);
    setValue('content', converted.html, { shouldValidate: true });
    if (!getValues('name')) {
      setValue('name', converted.filename.replace(/\.docx$/i, ''), { shouldValidate: true });
    }
  };

  const handleFormSubmit = async (data: PolicyFormData) => {
    try {
      await onSubmit({
        ...data,
        source_filename: sourceDoc?.filename ?? null,
        source_document: sourceDoc?.base64 ?? null,
      });
      reset();
      setSourceDoc(null);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component via toast
    }
  };

  const handleClose = () => {
    reset();
    setSourceDoc(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-4xl max-h-[80vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Create New Policy</DialogTitle>
          <DialogDescription>
            Create a new security policy document for your organization.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='name'>
                Policy Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='name'
                placeholder='e.g., Acceptable Use Policy'
                {...register('name')}
                disabled={isSubmitting}
              />
              {errors.name && <p className='text-sm text-destructive'>{errors.name.message}</p>}
            </div>

            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <Label htmlFor='content'>
                  Policy Content <span className='text-destructive'>*</span>
                </Label>
                <DocxUploadButton onConverted={handleDocxConverted} disabled={isSubmitting} />
              </div>
              {sourceDoc && (
                <p className='text-sm text-muted-foreground flex items-center gap-1'>
                  <FileText className='h-4 w-4' />
                  Imported from "{sourceDoc.filename}" — the original will be stored with the policy
                </p>
              )}
              <CKEditorWrapper
                content={content}
                onChange={(html) => setValue('content', html, { shouldValidate: true })}
                editable={!isSubmitting}
              />
              {errors.content && (
                <p className='text-sm text-destructive'>{errors.content.message}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Policy'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Delete Policy Modal
interface DeletePolicyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policy: PolicyResponse | null;
  onConfirm: (policyId: number) => Promise<void>;
  isDeleting: boolean;
}

export const DeletePolicyModal = ({
  open,
  onOpenChange,
  policy,
  onConfirm,
  isDeleting,
}: DeletePolicyModalProps) => {
  const handleConfirm = async () => {
    if (!policy) return;

    try {
      await onConfirm(policy.id);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component via toast
    }
  };

  if (!policy) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Policy?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the policy <strong>"{policy.name}"</strong>? This action
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDeleting}
            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
          >
            {isDeleting ? 'Deleting...' : 'Delete Policy'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
